import {
  AlertTriangle,
  CalendarClock,
  Check,
  ChevronRight,
  CircleMinus,
  CircleX,
  Clock3,
  Eye,
  ListChecks,
  MessageCircleQuestion,
  UserRoundCog,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { AgentThinkingOrb } from '@/frontend/features/AgentChats/assistant-ui/components/AgentThinkingOrb'
import { mergeBackgroundWork } from '@/frontend/features/AgentChats/events/backgroundWork'
import { useExecutionStatus } from '@/frontend/features/AgentChats/events/useExecutionStatus'
import type { ReactNode } from 'react'
import type {
  BackgroundWorkKind,
  BackgroundWorkPresentation,
  BackgroundWorkStatus,
  RunActivity,
} from '@/frontend/features/AgentChats/events/agentEvents'

export interface BackgroundWorkItem extends BackgroundWorkPresentation {
  id: string
  callIndex: number
}

function ExpiringWork({ item, children }: { item: BackgroundWorkItem; children: (item: BackgroundWorkItem) => ReactNode }) {
  const status = useExecutionStatus(item.status, item.statusValidUntil)
  return children(status === item.status ? item : {
    ...item, status, label: 'Status auxiliar não confirmado',
    detail: 'Não há confirmação atual do estado desta execução. Não é possível afirmar que continua em andamento.',
  })
}

// Start/result frames for the same concrete Attention OS object are folded,
// but each distinct object remains anchored at the call where it first
// appeared. This lets the conversation preserve the actual event sequence.
export function BackgroundWorkCard({
  activity,
  items: providedItems,
  onOpenActivity,
  onOpenDetails,
  variant = 'conversation',
}: {
  activity: RunActivity
  items?: BackgroundWorkItem[]
  onOpenActivity?: () => void
  onOpenDetails?: (item: BackgroundWorkItem) => void
  variant?: 'conversation' | 'panel'
}) {
  const items = providedItems || backgroundWorkItems(activity)
  if (items.length === 0) return null

  return (
    <div
      data-testid="agent-chat-background-work"
      data-variant={variant}
      className={cn('my-1 flex min-w-0 flex-col', variant === 'panel' ? 'gap-3' : 'gap-0.5')}
    >
      {items.map((original) => (
        <ExpiringWork key={original.id} item={original}>{(item) => (
        <article
          key={item.id}
          data-kind={item.kind}
          data-status={item.status}
          data-tone={statusTone(item)}
          className={cn(
            'group/work relative flex min-w-0 items-start gap-2.5',
            variant === 'panel'
              ? 'border-l border-border py-2 pl-3'
              : '-mx-2 rounded-lg px-2 py-1.5 transition-colors hover:bg-accent focus-within:bg-accent',
          )}
        >
          {variant === 'conversation' && onOpenActivity && (
            <button
              type="button"
              onClick={onOpenActivity}
              aria-label={`Ver atividade: ${displayLabel(item)}`}
              className="absolute inset-0 z-10 rounded-lg focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-brand/12"
            />
          )}
          <StatusIcon item={item} />
          <div className="min-w-0 flex-1">
            <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
              <p
                className={cn(
                  'text-[12.5px] leading-5',
                  variant === 'panel' ? 'font-medium text-foreground' : 'text-muted-foreground',
                )}
              >
                {displayLabel(item)}
              </p>
              {variant === 'panel' && (
                <span className="rounded-full bg-muted/55 px-1.5 py-0.5 text-[9.5px] text-faint-foreground">
                  {kindLabel(item.kind)}
                </span>
              )}
            </div>

            {customerFacingSubject(item) && (
              <p
                className={cn(
                  'mt-0.5 leading-[1.5] text-muted-foreground',
                  variant === 'panel' ? 'line-clamp-3 text-[11.5px]' : 'line-clamp-1 text-[11px]',
                )}
              >
                {customerFacingSubject(item)}
              </p>
            )}
            {variant === 'panel' && item.detail && (
              <p className="mt-0.5 text-[10.5px] leading-[1.45] text-faint-foreground">
                {item.detail}
              </p>
            )}
            {variant === 'panel' && item.currentStep && isActive(item.status) && (
              <p className="mt-1.5 flex items-start gap-1 text-[10.5px] leading-[1.45] text-muted-foreground">
                <ListChecks aria-hidden="true" strokeWidth={1.75} className="mt-0.5 size-3 shrink-0" />
                <span><span className="font-medium text-foreground/80">Agora:</span> {item.currentStep}</span>
              </p>
            )}
            {variant === 'panel' && item.dueAt && isActive(item.status) && (
              <p className="mt-1.5 flex items-center gap-1 text-[10px] tabular-nums text-faint-foreground">
                <CalendarClock aria-hidden="true" strokeWidth={1.75} className="size-3" />
                Prazo informado: {formatDueAt(item.dueAt)}
              </p>
            )}
            {variant === 'panel' && !item.dueAt && item.nextCheckAt && isActive(item.status) && (
              <p className="mt-1.5 flex items-center gap-1 text-[10px] tabular-nums text-faint-foreground">
                <Eye aria-hidden="true" strokeWidth={1.75} className="size-3" />
                Próxima verificação: {formatDueAt(item.nextCheckAt)}
              </p>
            )}
          </div>

          {variant === 'conversation' && onOpenActivity && (
            <ChevronRight
              aria-hidden="true"
              className="mt-0.5 size-3.5 shrink-0 text-faint-foreground opacity-60 transition-opacity group-hover/work:opacity-100"
            />
          )}
          {variant === 'panel' && ['parallel_analysis', 'subagent'].includes(item.kind) && item.runId && onOpenDetails && (
            <button
              type="button"
              data-testid="agent-chat-background-run-open"
              onClick={() => onOpenDetails(item)}
              className="mt-0.5 flex shrink-0 items-center gap-1 rounded-md px-1.5 py-1 text-[10.5px] font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-brand/12"
            >
              Ver trabalho
              <ChevronRight aria-hidden="true" className="size-3" />
            </button>
          )}
        </article>
        )}</ExpiringWork>
      ))}
    </div>
  )
}

export function backgroundWorkItems(activity: RunActivity): BackgroundWorkItem[] {
  const items = new Map<string, BackgroundWorkItem>()
  const unresolvedSubagents = new Map<string, string>()

  for (const [callIndex, call] of activity.toolCalls.entries()) {
    const work = call.backgroundWork
    if (!work) continue

    let id = work.sourceRef ||
      (work.taskId ? `${work.kind}:task:${work.taskId}` : undefined) ||
      (work.runId ? `${work.kind}:run:${work.runId}` : undefined) ||
      `${work.kind}:call:${call.toolCallId}`
    const subagentTaskKey = work.kind === 'subagent' && work.title
      ? work.title.trim().replace(/\s+/g, ' ').toLocaleLowerCase('pt-BR')
      : undefined
    let current = items.get(id)
    if (subagentTaskKey && !work.runId) {
      const unresolvedId = unresolvedSubagents.get(subagentTaskKey)
      if (unresolvedId) {
        id = unresolvedId
        current = items.get(id)
      } else {
        unresolvedSubagents.set(subagentTaskKey, id)
      }
    } else if (subagentTaskKey && work.runId) {
      const unresolvedId = unresolvedSubagents.get(subagentTaskKey)
      if (unresolvedId && unresolvedId !== id) {
        current = items.get(unresolvedId)
        items.delete(unresolvedId)
        unresolvedSubagents.delete(subagentTaskKey)
      }
    }
    const observed = mergeBackgroundWork(current, work)!
    items.set(id, {
      ...current,
      ...work,
      id,
      callIndex: current?.callIndex ?? callIndex,
      title: work.kind === 'subagent' ? (current?.title || work.title) : (work.title || current?.title),
      status: observed.status,
      label: observed.label,
      detail: observed.detail || work.detail || current?.detail,
      executionOnly: observed.executionOnly,
      statusObservedAt: observed.statusObservedAt,
      statusValidUntil: observed.statusValidUntil,
    })
  }

  return [...items.values()]
}

function customerFacingSubject(item: BackgroundWorkItem): string | undefined {
  // Attention OS titles can be internal routing identifiers. Its explicit
  // deliverable is the customer-facing contract; when absent, the compact row
  // stays intentionally terse instead of leaking internal naming.
  if (['persistent_work', 'delivery_commitment', 'attention'].includes(item.kind)) {
    return item.expectedDeliverable
  }

  return item.expectedDeliverable || item.title
}

function kindLabel(kind: BackgroundWorkKind): string {
  if (kind === 'persistent_work') return 'Acompanhamento'
  if (kind === 'delivery_commitment' || kind === 'attention') return 'Retorno combinado'
  if (kind === 'consultation') return 'Consulta acompanhada'
  if (kind === 'conversation_watch') return 'Conversa acompanhada'
  if (kind === 'subagent') return 'Agente auxiliar'
  return 'Trabalho paralelo'
}

function displayLabel(item: BackgroundWorkItem): string {
  if (item.executionOnly || item.status === 'unknown') return item.label
  if (!['parallel_analysis', 'subagent', 'conductor'].includes(item.kind)) return item.label
  if (item.status === 'completed') return 'Trabalho concluído'
  if (item.status === 'failed' || item.status === 'blocked') return 'Trabalho interrompido'
  if (item.status === 'cancelled') return 'Trabalho cancelado'
  if (item.status === 'waiting' || item.status === 'needs_user') return 'Trabalho aguardando confirmação'
  return 'Trabalho em andamento'
}

function isActive(status: BackgroundWorkStatus): boolean {
  return ['starting', 'running', 'watching', 'waiting', 'needs_user'].includes(status)
}

function statusTone(item: BackgroundWorkItem): 'neutral' | 'danger' | 'success' | 'active' {
  if (item.status === 'unknown') return 'neutral'
  if (item.status === 'blocked' && item.routeStatus === 'unavailable') return 'neutral'
  if (item.status === 'failed' || item.status === 'blocked') return 'danger'
  if (item.status === 'completed') return 'success'
  return 'active'
}

function StatusIcon({ item }: { item: BackgroundWorkItem }) {
  const { status } = item
  const className = 'mt-1 size-3.5 shrink-0'

  if (status === 'blocked' && item.routeStatus === 'unavailable') {
    return <CircleMinus aria-hidden="true" strokeWidth={1.75} className={`${className} text-faint-foreground`} />
  }
  if (status === 'failed' || status === 'blocked') {
    return <AlertTriangle aria-hidden="true" className={`${className} text-destructive`} />
  }
  if (status === 'completed') {
    return <Check aria-hidden="true" strokeWidth={1.75} className={`${className} text-success`} />
  }
  if (status === 'cancelled' || status === 'unknown') {
    return <CircleX aria-hidden="true" strokeWidth={1.75} className={`${className} text-faint-foreground`} />
  }
  if (status === 'needs_user') {
    return <MessageCircleQuestion aria-hidden="true" strokeWidth={1.75} className={`${className} text-agent`} />
  }
  if (status === 'waiting' || status === 'watching') {
    return <Clock3 aria-hidden="true" strokeWidth={1.75} className={`${className} text-agent`} />
  }
  if (status === 'running' || status === 'starting') {
    return <AgentThinkingOrb className={className} label="Trabalho em paralelo" />
  }
  return <UserRoundCog aria-hidden="true" strokeWidth={1.75} className={`${className} text-agent`} />
}

function formatDueAt(value: string): string {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value

  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date)
}
